import React, { useState } from 'react';
import { Plus, Minus } from 'lucide-react';

const FAQS = [
  {
    q: 'Is this real compliance, or just templates?',
    a: 'It is both. Verilock generates a compliance roadmap and three fully written policy documents tailored to your specific business, your stack, your data, your risk profile. It is not a generic template. It is built from your answers. That said, it is a compliance foundation, not a full SOC2 audit. When you are ready for a formal audit, we will tell you exactly how to get there.',
  },
  {
    q: 'Will enterprise prospects actually accept this?',
    a: 'Most procurement teams at the early deal stage want to see that you take security seriously, policies in place, a documented posture, and a page they can share internally. That is exactly what Verilock gives you. For deals requiring a full SOC2 Type II report, you will need a formal audit eventually. Verilock gets you ready for that conversation.',
  },
  {
    q: 'How is this different from Vanta or Sprinto?',
    a: 'Vanta and Sprinto are built for Series A+ companies with $10K-$30K/year budgets and a dedicated security team. Verilock is built for founders who need something credible today, not a 6-month compliance project. Different customer, different price, different purpose.',
  },
  {
    q: 'What happens to my data?',
    a: 'Your wizard answers and generated documents are stored securely in our database and never shared with third parties. You can delete your account and all associated data at any time by emailing hello@verilock.ai.',
  },
  {
    q: 'Can I cancel anytime?',
    a: 'Yes. Cancel from your dashboard settings at any time. No contracts, no cancellation fees. Your subscription ends at the end of the current billing period.',
  },
  {
    q: 'What if I already have some compliance documentation?',
    a: 'Even better. Verilock will identify gaps in your existing posture and generate the pieces you are missing. Your compliance score will reflect what you already have in place.',
  },
  {
    q: 'Do I need a technical background to use this?',
    a: 'Not at all. The wizard asks plain English questions about your business. No security expertise required. If you can describe what your product does and how you handle customer data, you can complete it.',
  },
  {
    q: "Is this enough to satisfy a prospect's security review?",
    a: "For most early-stage deals, yes. Verilock gives you a shareable Trust Page with your active policies and compliance status, exactly what a prospect's security team asks for. It won't replace a full SOC2 audit, but it will stop you from losing deals while you're still pre-revenue.",
  },
  {
    q: 'What happens if I cancel?',
    a: 'You can cancel anytime from your dashboard. Your Trust Page stays live until the end of your billing period, then becomes inactive. You keep access to any policies you downloaded.',
  },
];

const Faq = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section
      data-testid="landing-faq"
      className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20"
    >
      <div className="text-center mb-10 sm:mb-14">
        <h3
          className="text-3xl sm:text-4xl lg:text-5xl font-normal leading-tight"
          style={{ fontFamily: 'Instrument Serif, serif' }}
        >
          Questions we get asked a lot
        </h3>
      </div>

      <div className="divide-y divide-[#e0ddd8] border-t border-b border-[#e0ddd8]">
        {FAQS.map((item, idx) => {
          const isOpen = openIdx === idx;
          return (
            <div key={item.q} data-testid={`faq-item-${idx}`}>
              <button
                type="button"
                onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                aria-expanded={isOpen}
                data-testid={`faq-toggle-${idx}`}
                className="w-full flex items-start justify-between gap-4 text-left py-5 sm:py-6 px-1 hover:text-[#0a5c44] transition-colors group"
              >
                <span
                  className="text-lg sm:text-xl font-normal text-[#1a1a1a] group-hover:text-[#0a5c44] leading-snug transition-colors"
                  style={{ fontFamily: 'Instrument Serif, serif' }}
                >
                  {item.q}
                </span>
                <span
                  className="shrink-0 mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#0a5c44]/30 text-[#0a5c44] transition-transform"
                  aria-hidden="true"
                >
                  {isOpen ? (
                    <Minus className="w-4 h-4" />
                  ) : (
                    <Plus className="w-4 h-4" />
                  )}
                </span>
              </button>
              <div
                className={`grid transition-all duration-300 ease-out ${
                  isOpen ? 'grid-rows-[1fr] opacity-100 pb-5 sm:pb-6' : 'grid-rows-[0fr] opacity-0'
                }`}
              >
                <div className="overflow-hidden">
                  <p
                    data-testid={`faq-answer-${idx}`}
                    className="text-sm sm:text-base text-[#1a1a1a]/75 leading-relaxed pr-10"
                  >
                    {item.a}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default Faq;
